import * as THREE from 'three';
import { FBXLoader } from 'three/addons/loaders/FBXLoader.js';
import { disposeObject } from './dispose.ts';
import { readFbxSettings, type FbxSettings } from './fbxMeta.ts';

export type ItemKind = 'skeleton' | 'prop';

/**
 * One addressable thing in a Motive take: an actor skeleton or a prop / rigid
 * body. Each lives under its own top-level group, which we use as the mixer root
 * so its (often duplicate-named) animation tracks bind unambiguously.
 */
export interface TakeItem {
  id: string;
  label: string;
  kind: ItemKind;
  /** Per-item group (mixer root) that uniquely contains this item's nodes. */
  root: THREE.Object3D;
  bones: THREE.Object3D[];
  hips: THREE.Object3D | null;
}

export interface LoadedMocap {
  object: THREE.Group;
  rootBone: THREE.Object3D | null;
  bones: THREE.Object3D[];
  /** Every actor + prop in the take, labelled and ordered. */
  items: TakeItem[];
  clip: THREE.AnimationClip | null;
  hasMesh: boolean;
  /** Frame rate declared in the file (or estimated from key spacing), if known. */
  nativeFps: number | null;
  /** True when the clip's key times were rescaled to match the declared rate. */
  retimed: boolean;
}

const fbxLoader = new FBXLoader();

/**
 * Parse a mocap FBX (OptiTrack Motive or Move.AI) from an ArrayBuffer, normalize
 * its up-axis/scale/grounding, and split it into addressable items (actor
 * skeletons + props).
 */
export function parseMocapFbx(buffer: ArrayBuffer): LoadedMocap {
  // GlobalSettings metadata (up axis, frame rate) — FBXLoader parses but drops it.
  const settings = readFbxSettings(buffer);

  // FBXLoader.parse takes an ArrayBuffer (binary) or string (ascii) + resource path.
  const object = fbxLoader.parse(buffer, '');

  stripMoveAiVisuals(object);

  const bones: THREE.Object3D[] = [];
  let hasMesh = false;
  object.traverse((child) => {
    if ((child as THREE.Bone).isBone) bones.push(child);
    if ((child as THREE.Mesh).isMesh) hasMesh = true;
  });

  // detectItems only reads the hierarchy, so it can run before normalization —
  // normalizeTransform needs the skeleton items for its up-axis heuristic.
  const items = detectItems(bones);
  normalizeTransform(object, bones, hasMesh, items, settings);
  const rootBone = items.find((i) => i.kind === 'skeleton')?.hips ?? findRootBone(bones);
  const clip = object.animations.length > 0 ? object.animations[0] : null;

  const nativeFps = settings.frameRate ?? (clip ? estimateFpsFromKeys(clip) : null);
  const retimed = clip && nativeFps ? fixClipTiming(clip, nativeFps) : false;

  return { object, rootBone, bones, items, clip, hasMesh, nativeFps, retimed };
}

/**
 * Dominant key-time spacing of a clip, from a histogram of per-track deltas
 * (bucketed at 0.1 ms). Returns the spacing and the fraction of all deltas that
 * fall in that bucket — near 1.0 means uniformly resampled (one key per frame).
 */
function dominantKeyDelta(clip: THREE.AnimationClip): { delta: number; coverage: number } | null {
  const counts = new Map<number, number>();
  let total = 0;
  for (const track of clip.tracks) {
    for (let i = 1; i < track.times.length; i++) {
      const d = track.times[i] - track.times[i - 1];
      if (d <= 0) continue;
      const bucket = Math.round(d * 10000);
      counts.set(bucket, (counts.get(bucket) ?? 0) + 1);
      total++;
    }
  }
  if (total === 0) return null;
  let bestBucket = 0;
  let bestCount = 0;
  for (const [bucket, count] of counts) {
    if (count > bestCount) {
      bestCount = count;
      bestBucket = bucket;
    }
  }
  return { delta: bestBucket / 10000, coverage: bestCount / total };
}

/** Estimate fps from key spacing when the file declares no frame rate. */
function estimateFpsFromKeys(clip: THREE.AnimationClip): number | null {
  const dom = dominantKeyDelta(clip);
  if (!dom || dom.coverage < 0.8 || dom.delta <= 0) return null;
  return Math.round(1 / dom.delta);
}

/**
 * Move.AI exporter bug: keys are written one per frame of the declared rate
 * (CustomFrameRate 180) but their KTime stamps sit on a ~12.5 fps grid, so the
 * clip plays 14.4x too slow in any FBX-conformant player. When the clip is
 * uniformly sampled and its key spacing disagrees with the declared rate,
 * rescale all track times so one key = one frame at the declared rate.
 * (Verified against motion speed: hips velocity distributions only match the
 * known-good Motive takes after rescaling.)
 */
function fixClipTiming(clip: THREE.AnimationClip, nativeFps: number): boolean {
  const dom = dominantKeyDelta(clip);
  if (!dom || dom.coverage < 0.8) return false; // reduced keys — can't infer a grid
  const framesPerKey = dom.delta * nativeFps;
  if (Math.abs(framesPerKey - 1) < 0.05) return false; // stamps already match the rate
  const scale = 1 / framesPerKey;
  for (const track of clip.tracks) {
    for (let i = 0; i < track.times.length; i++) track.times[i] *= scale;
  }
  clip.resetDuration();
  return true;
}

/**
 * Move.AI exports ship URDF-style skinned `visual_*` mannequin meshes and an
 * empty `urdf_fbx` group alongside the `_N`-prefixed actor skeletons. Strip them
 * so the take behaves like a bone-only Motive file — the app's own puppets/VRMs
 * supply meshes, and a mesh-bearing FBX would otherwise be mis-routed as a
 * character mesh when a source is already loaded.
 */
function stripMoveAiVisuals(object: THREE.Group): void {
  let isMoveAi = false;
  object.traverse((child) => {
    if ((child as THREE.Bone).isBone && /^_\d+/.test(child.name)) isMoveAi = true;
  });
  if (!isMoveAi) return;

  const doomed: THREE.Object3D[] = [];
  object.traverse((child) => {
    if (((child as THREE.Mesh).isMesh && /^visual_/.test(child.name)) || child.name === 'urdf_fbx')
      doomed.push(child);
  });
  for (const node of doomed) {
    node.removeFromParent();
    disposeObject(node);
  }
}

/**
 * Group bones by their top-most bone ancestor into items. A group with enough
 * bones (or a hips) is an actor skeleton; a tiny group is a prop / rigid body.
 */
function detectItems(bones: THREE.Object3D[]): TakeItem[] {
  const groups = new Map<THREE.Object3D, THREE.Object3D[]>();
  for (const bone of bones) {
    let top = bone;
    while (top.parent && (top.parent as THREE.Bone).isBone) top = top.parent;
    const list = groups.get(top);
    if (list) list.push(bone);
    else groups.set(top, [bone]);
  }

  // Motive parents each actor's top bone to its own "Root" group, but Move.AI
  // parents every actor's root bone to ONE shared group. Rooting two items (and
  // their mixers/helpers) at that shared group would make each item claim the
  // other's nodes, so items whose top bones share a parent root at the top bone
  // itself instead (their track names are unique per actor, and PropertyBinding
  // matches the mixer root by name, so the top bone's own tracks still bind).
  const topsPerParent = new Map<THREE.Object3D | null, number>();
  for (const top of groups.keys()) {
    topsPerParent.set(top.parent, (topsPerParent.get(top.parent) ?? 0) + 1);
  }

  const skeletons: TakeItem[] = [];
  const props: TakeItem[] = [];
  for (const [top, groupBones] of groups) {
    const hips = groupBones.find((b) => /hips?$|pelvis/i.test(b.name)) ?? null;
    const isSkeleton = groupBones.length >= 10 || hips !== null;
    // The per-item group (the FBX "Root") is the topmost bone's parent when
    // present and not shared with another item.
    const sharedParent = (topsPerParent.get(top.parent) ?? 0) > 1;
    const root = !sharedParent && top.parent ? top.parent : top;
    if (isSkeleton) {
      const n = skeletons.length + 1;
      skeletons.push({
        id: `skeleton-${n}`,
        label: `Skeleton ${String(n).padStart(3, '0')}`,
        kind: 'skeleton',
        root,
        bones: groupBones,
        hips: hips ?? top,
      });
    } else {
      const n = props.length + 1;
      props.push({
        id: `prop-${n}`,
        label: prettifyName(top.name) || `Rigid Body ${String(n).padStart(3, '0')}`,
        kind: 'prop',
        root,
        bones: groupBones,
        hips: null,
      });
    }
  }
  // Skeletons first, then props (props are secondary in the panel).
  return [...skeletons, ...props];
}

/** "Rigid_Body_001Bone" → "Rigid Body 001". */
function prettifyName(name: string): string {
  return name
    .replace(/Bone$/, '')
    .replace(/[_]+/g, ' ')
    .trim();
}

/**
 * Split a multi-actor clip into one sub-clip per item. Tracks share names across
 * actors (e.g. three "Hips.quaternion"), so we assign the k-th occurrence of a
 * node name to the k-th item that owns a node of that name (FBX emits them in
 * matching order). Each sub-clip is then driven by a mixer rooted at its item.
 */
export function buildItemClips(
  clip: THREE.AnimationClip,
  items: TakeItem[]
): Map<TakeItem, THREE.AnimationClip> {
  // node name -> items (in order) that contain a node of that name.
  const owners = new Map<string, TakeItem[]>();
  for (const item of items) {
    item.root.traverse((node) => {
      if (!node.name) return;
      const list = owners.get(node.name);
      if (list) list.push(item);
      else owners.set(node.name, [item]);
    });
  }

  const tracksByItem = new Map<TakeItem, THREE.KeyframeTrack[]>();
  // Key the occurrence counter by the FULL track name (e.g. "Hips.quaternion"),
  // not just the node — a bone has several tracks (.position/.quaternion), and
  // the k-th occurrence of each maps to the k-th item owning that node.
  const seen = new Map<string, number>();
  for (const track of clip.tracks) {
    const nodeName = track.name.slice(0, track.name.lastIndexOf('.'));
    const occ = seen.get(track.name) ?? 0;
    seen.set(track.name, occ + 1);
    const item = owners.get(nodeName)?.[occ];
    if (!item) continue;
    const list = tracksByItem.get(item);
    if (list) list.push(track);
    else tracksByItem.set(item, [track]);
  }

  const result = new Map<TakeItem, THREE.AnimationClip>();
  for (const item of items) {
    const tracks = tracksByItem.get(item);
    if (tracks && tracks.length > 0) {
      result.set(item, new THREE.AnimationClip(item.id, clip.duration, tracks));
    }
  }
  return result;
}

/** World-position bounding box of a set of bones (bones have no geometry). */
function boxFromBones(bones: THREE.Object3D[]): THREE.Box3 {
  const box = new THREE.Box3();
  const p = new THREE.Vector3();
  for (const bone of bones) {
    bone.getWorldPosition(p);
    box.expandByPoint(p);
  }
  return box;
}

/**
 * Mocap FBX is typically authored in centimeters and arrives ~100x too large;
 * Move.AI additionally authors Z-up, which FBXLoader does not convert. Stand the
 * rig up in Y, scale to roughly human size, and drop it so its feet rest on the
 * Y=0 grid.
 */
function normalizeTransform(
  object: THREE.Object3D,
  bones: THREE.Object3D[],
  hasMesh: boolean,
  items: TakeItem[],
  settings: FbxSettings
): void {
  object.updateWorldMatrix(true, true);

  // Up-axis heuristic: at bind a humanoid skeleton stands along its authoring up
  // axis, so a rig whose Z extent dwarfs its Y extent was authored Z-up (Move.AI).
  // Judged from actor skeletons only: a lone prop's proportions say nothing about
  // the take's up axis.
  const skeletonBones = items.filter((i) => i.kind === 'skeleton').flatMap((i) => i.bones);
  let heuristicZUp = false;
  if (skeletonBones.length > 0) {
    const size = new THREE.Vector3();
    boxFromBones(skeletonBones).getSize(size);
    heuristicZUp = size.z > size.y * 2;
  }
  // GlobalSettings UpAxis (2 = Z) is authoritative when present, but only rotate
  // a skeleton take when the bind pose agrees — some exporters bake the content
  // Y-up and leave stale metadata. Prop-only takes trust the metadata outright.
  const zUp =
    settings.upAxis === 2
      ? skeletonBones.length === 0 || heuristicZUp
      : settings.upAxis === undefined && heuristicZUp;
  if (zUp) {
    object.rotateX(-Math.PI / 2);
    object.updateWorldMatrix(true, true);
  }

  const box = hasMesh ? new THREE.Box3().setFromObject(object) : boxFromBones(bones);

  if (box.isEmpty()) return;

  const size = new THREE.Vector3();
  box.getSize(size);
  const height = size.y || Math.max(size.x, size.z);

  // If clearly in centimeters (tall rig), bring down to metres.
  if (height > 10) {
    object.scale.multiplyScalar(0.01);
  }

  // Re-measure after scaling, then ground the rig at Y = 0.
  object.updateWorldMatrix(true, true);
  const grounded = hasMesh ? new THREE.Box3().setFromObject(object) : boxFromBones(bones);
  if (!grounded.isEmpty()) {
    object.position.y -= grounded.min.y;
    object.updateWorldMatrix(true, true);
  }
}

/**
 * Find the skeleton root for OrbitControls targeting. Prefer a hips/pelvis bone,
 * else the topmost bone in the hierarchy.
 */
function findRootBone(bones: THREE.Object3D[]): THREE.Object3D | null {
  if (bones.length === 0) return null;
  const hips = bones.find((b) => /hip|pelvis|root/i.test(b.name));
  if (hips) return hips;

  // Fallback: bone with the fewest bone ancestors.
  let best = bones[0];
  let bestDepth = Infinity;
  for (const bone of bones) {
    let depth = 0;
    let p = bone.parent;
    while (p) {
      if ((p as THREE.Bone).isBone) depth++;
      p = p.parent;
    }
    if (depth < bestDepth) {
      bestDepth = depth;
      best = bone;
    }
  }
  return best;
}
